import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { romeDay } from '@buzzmap/shared';
import {
  cancelReturnVisitWork,
  isEligibleReturnVisitPath,
  recordReturnVisit,
} from '../../lib/returnVisits';

/**
 * ReturnVisitRecorder (T008-FE React half, plan
 * `docs/IMPLEMENTATION_PLAN_RETURN_VISITS.md` §"Browser/ingress protocol and
 * delivery semantics"). Mounted ONCE inside the PUBLIC route tree (App.tsx,
 * inside BrowserRouter so `useLocation` observes the real current path);
 * renders null and never touches layout.
 *
 * Triggers — the component decides WHEN an observation attempt fires; the
 * state/transport module (`src/lib/returnVisits.ts`) owns every guard
 * (build flag, host, eligible path, visibility, preference, origin-wide
 * lock, fresh config, reservation, bounded POST):
 * - visible mount of an eligible route;
 * - `pageshow` (bfcache restore);
 * - `visibilitychange` → visible;
 * - SPA navigation EXCLUDED → ELIGIBLE (e.g. /cookie-policy → / on the SAME
 *   day — the first eligible visit records immediately, not tomorrow);
 * - genuine SPA navigation after the Europe/Rome day changed since the last
 *   attempt. Being idle across midnight without any trigger is NOT a
 *   return: no timers exist anywhere in this flow.
 * A same-day ELIGIBLE → ELIGIBLE navigation is deliberately NOT a trigger
 * (the lib would only re-fetch the config to no-op); ELIGIBLE → EXCLUDED
 * never contributes. StrictMode's double effect invoke is treated as a
 * re-mount of the same location so a cancelled first attempt is retried
 * once; the lib's per-instance queue + origin-wide lock guarantee at most
 * ONE effective attempt (at most one POST) per trigger.
 *
 * Contract: never throws, never logs, never gates rendering — every call is
 * fire-and-forget (`recordReturnVisit` resolves by design) and wrapped so a
 * misbehaving lib cannot break the app. With the build flag OFF nothing is
 * touched at all: no listeners, no lib calls, no document reads. Unmount
 * removes both listeners and calls `cancelReturnVisitWork()` (aborts
 * in-flight config/POST work, bumps the async generation).
 */

/** Visible means neither `hidden` nor the prerender state (plan guard order). */
function documentIsVisible(): boolean {
  try {
    if (document.hidden) return false;
    const state = document.visibilityState as string | undefined;
    return state !== 'hidden' && state !== 'prerender';
  } catch {
    return false;
  }
}

/**
 * One observation attempt, gated the same way the lib gates internally so a
 * skipped trigger never reaches the lock/network path at all. Records the
 * attempted Rome day for the day-change trigger.
 */
function attemptRecord(pathname: string, lastDayRef: { current: string | null }): void {
  if (!isEligibleReturnVisitPath(pathname)) return;
  if (!documentIsVisible()) return;
  try {
    void recordReturnVisit();
    lastDayRef.current = romeDay(new Date());
  } catch {
    // Silent by contract: a recorder failure must never reach the console
    // (and never carry data) nor affect rendering.
  }
}

export function ReturnVisitRecorder() {
  const location = useLocation();
  const enabled = import.meta.env.VITE_RETURN_VISITS_ENABLED === 'true';

  // Latest pathname for the event listeners (registered once per mount).
  // Written in the navigation effect below, never during render (review S1);
  // the listeners only read it after the effect that tracked the navigation.
  const pathnameRef = useRef(location.pathname);
  // Previous navigation identity — null only before the first effect run;
  // a same key+pathname re-run means a StrictMode re-mount, not a navigation.
  const prevRef = useRef<{ key: string; pathname: string } | null>(null);
  // Rome day of the last attempt, for the "SPA activity after day change" trigger.
  const lastDayRef = useRef<string | null>(null);

  // Navigation trigger: visible mount + EXCLUDED→ELIGIBLE + day change.
  useEffect(() => {
    if (!enabled) return;
    const current = location.pathname;
    pathnameRef.current = current;
    const prev = prevRef.current;
    prevRef.current = { key: location.key, pathname: current };
    if (!isEligibleReturnVisitPath(current)) return; // EXCLUDED: never record
    const remount = prev === null || (prev.key === location.key && prev.pathname === current);
    const becameEligible =
      prev !== null && prev.pathname !== current && !isEligibleReturnVisitPath(prev.pathname);
    const dayChanged = lastDayRef.current !== romeDay(new Date());
    if (remount || becameEligible || dayChanged) {
      attemptRecord(current, lastDayRef);
    }
  }, [enabled, location]);

  // Browser-event triggers + unmount cleanup. Registered once per mount so
  // navigations never re-attach (no duplicate listeners, no duplicate calls).
  useEffect(() => {
    if (!enabled) return;
    const onPageShow = () => attemptRecord(pathnameRef.current, lastDayRef);
    const onVisibilityChange = () => {
      if (documentIsVisible()) attemptRecord(pathnameRef.current, lastDayRef);
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cancelReturnVisitWork();
    };
  }, [enabled]);

  return null;
}
